/**
 * ISO 20022 Message Sample Data
 * 
 * This file contains sample JSON structures and validation rules for each supported
 * message type. Add new message types or update existing samples here.
 * 
 * Structure:
 * - name: Display name of the message type
 * - description: Brief description of the message purpose
 * - sample: Complete JSON structure that can be used as a template
 * - validationRules: Frontend validation rules (required fields, nested validations)
 */

export const MESSAGE_SAMPLES = {
    'pacs.008': {
      name: 'Payment Instruction (pacs.008)',
      description: 'Customer credit transfer between financial institutions',
      sample: {
        grpHdr: {
          msgId: "MSG-DEC-0005",
          creDtTm: "2025-12-02T20:09:00Z",
          nbOfTxs: "1",
          sttlmInf: { sttlmMtd: "CLRG" },
          instgAgt: {
            finInstnId: {
              othr: { id: "040" }
            }
          },
          instdAgt: {
            finInstnId: {
              othr: { id: "078" }
            }
          }
        },
        cdtTrfTxInf: [{
          pmtId: {
            endToEndId: "E2E-TST-DEC-005",
            instrId: "INSTR-TEST-DEC-005",
            txId: "TX-TEST-DEC-005"
          },
          intrBkSttlmAmt: {
            value: "1250.00",
            ccy: "RWF"
          },
          dbtrAgt: {
            finInstnId: {
              othr: { id: "078" }
            }
          },
          dbtr: { nm: "Mike Tango" },
          dbtrAcct: {
            id: {
              othr: { id: "0788123456" }
            }
          },
          cdtrAgt: {
            finInstnId: {
              othr: { id: "040" }
            }
          },
          cdtr: { nm: "Alpha Romeo" },
          cdtrAcct: {
            id: {
              othr: { id: "000400012345678" }
            }
          }
        }]
      },
      validationRules: {
        required: ['grpHdr', 'cdtTrfTxInf'],
        grpHdr: ['msgId', 'creDtTm', 'nbOfTxs', 'sttlmInf'],
        cdtTrfTxInf: ['pmtId', 'intrBkSttlmAmt', 'dbtr', 'cdtr'],
        pmtId: ['endToEndId', 'txId']
      }
    },
  
    'pacs.002': {
      name: 'Payment Status Report (pacs.002)',
      description: 'Status report for payment instructions',
      sample: {
        grpHdr: {
          msgId: "STS-20251202-0005",
          creDtTm: "2025-12-02T20:12:00Z"
        },
        orgnlGrpInfo: {
          orgnlMsgId: "MSG-DEC-0005",
          orgnlMsgNmId: "pacs.008.001.11",
          grpSts: "ACCP"
        },
        txInfAndSts: [{
          orgnlInstrId: "INSTR-TEST-DEC-005",
          orgnlInstrEndToEndId: "E2E-TST-DEC-005",
          orgnlTxId: "TX-TEST-DEC-005",
          txSts: "ACSC",
          stsRsnInf: {
            rsn: {
              cd: "G000",
              prtry: "Payment pending"
            }
          },
          acceptanceDateTime: "2025-12-02T20:12:58Z"
        }]
      },
      validationRules: {
        required: ['grpHdr', 'orgnlGrpInfo', 'txInfAndSts'],
        grpHdr: ['msgId', 'creDtTm'],
        orgnlGrpInfo: ['orgnlMsgId', 'orgnlMsgNmId'],
        txInfAndSts: ['orgnlEndToEndId', 'txSts']
      }
    },
  
    'acmt.007': {
      name: 'Account Registration Request (acmt.007)',
      description: 'Request to register or open a new account',
      sample: {
        references: {
          messageId: {
            id: "acmt007-Dec-02-0010",
            creationDateTime: "2025-12-02T11:54:00Z"
          },
          processId: {
            id: "PROC-DEC-010",
            creationDateTime: "2025-12-02T11:54:00Z"
          },
          attchdDocNm: "pass3938449"
        },
        account: {
          id: {
            othr: {
              id: "ACC-00010"
            }
          },
          name: "Alpha Romeo",
          status: "ENAB",
          currency: "RWF"
        },
        acctSvcrId: {
          finInstnId: {
            othr: {
              id: "040"
            }
          }
        },
        organization: {
          fullLegalName: "Alpha S. Romeo",
          countryOfOperation: "RW",
          registrationDate: "2000-01-01",
          lglAdr: {
            adrTp: {
              cd: "ADDR"
            }
          },
          orgId: {
            othr: {
              id: "000432393994344"
            }
          },
          sndr: {
            pstlAdr: {
              adrTp: {
                cd: "BIZZ"
              }
            },
            id: {
              dtAndPlcOfBirth: {
                birthDt: "1970-01-01",
                cityOfBirth: "KGL",
                ctryOfBirth: "RW"
              },
              othr: {
                id: "TEST-A"
              }
            },
            ctctDtls: {
              nm: "Alpha Romeo",
              mobNb: "+250-780123456",
              emailAdr: "alpharomeo@home.rw",
              dept: "personal",
              othr: {
                chanlTp: "MAIL",
                id: "000432393994344"
              }
            }
          }
        },
        mndt: {
          id: "000432393994344",
          aplblChanl: {
            cd: "PHON"
          },
          reqrdSgntrNb: "1",
          sgntrOrdrInd: "false",
          mndtHldr: {
            ptyOrGrp: {
              grpId: "AUTH"
            },
            authstn: {
              maxAmtByTx: {
                amt: {
                  value: "50",
                  ccy: "RWF"
                }
              }
            }
          },
          bkOpr: {
            domn: {
              cd: "PMNT",
              fmly: {
                cd: "OTHR",
                subFmlyCd: "OTHR"
              }
            }
          }
        }
      },
      validationRules: {
        required: ['references', 'account', 'acctSvcrId'],
        references: ['messageId'],
        account: ['id', 'name', 'currency'],
        messageId: ['id', 'creationDateTime']
      }
    },
  
    'camt.003': {
      name: 'Account Lookup Request (camt.003)',
      description: 'Request to retrieve account information',
      sample: {
        msgHdr: {
          msgId: "CAMT003-REQ-0001",
          creDtTm: "2025-12-02T12:00:00Z"
        },
        acctQryDef: {
          acctCrit: {
            newCrit: {
              schCrit: {
                acctId: {
                  othr: {
                    id: "0788123456"
                  }
                }
              }
            }
          }
        }
      },
      validationRules: {
        required: ['grpHdr', 'acctQryDef'],
        grpHdr: ['msgId', 'creDtTm'],
        acctQryDef: ['acctCrit']
      }
    },

    'acmt.003': {
      name: 'Account Modification (acmt.003)',
      description: 'Request to modify details of an existing account',
      sample: {
        msgId: {
          id: "ACMT003-MOD-0001",
          creDtTm: "2026-06-24T10:00:00Z"
        },
        instrDtls: {
          id: "INSTR-MOD-0001",
          existingAccountId: "ACC-00010"
        },
        invstmtAcctSelctn: {
          id: {
            id: {
              othr: { id: "ACC-00010" }
            }
          }
        },
        modfdAcctPties: {
          scopeIndication: "INSE",
          acctSvcrId: {
            finInstnId: { othr: { id: "040" } }
          },
          acctOwnr: {
            indvPrsn: {
              nm: "Jane Doe",
              dtAndPlcOfBirth: {
                birthDt: "1985-03-15",
                cityOfBirth: "Kigali",
                ctryOfBirth: "RW"
              }
            }
          }
        }
      },
      validationRules: {
        required: ['msgId'],
        msgId: ['id', 'creDtTm']
      }
    },

    'acmt.019': {
      name: 'Account Closure Request (acmt.019)',
      description: 'Request to close a customer account',
      sample: {
        refs: {
          msgId: {
            id: "ACMT019-CLO-0001",
            creDtTm: "2026-06-24T10:00:00Z"
          },
          prcId: {
            id: "PROC-CLO-0001",
            creDtTm: "2026-06-24T10:00:00Z"
          }
        },
        acctId: {
          id: {
            othr: { id: "ACC-00010" }
          },
          nm: "Jane Doe",
          ccy: "RWF"
        },
        acctSvcrId: {
          finInstnId: { othr: { id: "040" } }
        },
        orgId: {
          fullLegalNm: "Jane Doe",
          ctryOfOpr: "RW"
        }
      },
      validationRules: {
        required: ['refs', 'acctId'],
        refs: ['msgId'],
        msgId: ['id', 'creDtTm']
      }
    },

    'pacs.028': {
      name: 'Payment Status Request (pacs.028)',
      description: 'Request the status of a previously submitted payment instruction',
      sample: {
        grpHdr: {
          msgId: "PACS028-REQ-0001",
          creDtTm: "2026-06-24T10:00:00Z",
          nbOfTxs: "1",
          instgAgt: { finInstnId: { othr: { id: "078" } } },
          instdAgt: { finInstnId: { othr: { id: "040" } } }
        },
        orgnlGrpInf: {
          orgnlMsgId: "MSG-ORIG-001",
          orgnlMsgNmId: "pacs.008.001.11",
          orgnlCreDtTm: "2026-06-23T09:00:00Z"
        },
        txInf: [{
          orgnlInstrId: "INSTR-ORIG-001",
          orgnlEndToEndId: "E2E-ORIG-001",
          orgnlTxId: "TX-ORIG-001",
          instgAgt: { finInstnId: { othr: { id: "078" } } },
          instdAgt: { finInstnId: { othr: { id: "040" } } }
        }]
      },
      validationRules: {
        required: ['grpHdr', 'orgnlGrpInf'],
        grpHdr: ['msgId', 'creDtTm'],
        orgnlGrpInf: ['orgnlMsgId', 'orgnlMsgNmId']
      }
    },

    'pain.013': {
      name: 'Request to Pay (pain.013)',
      description: 'Creditor payment activation request — initiates a Request to Pay flow',
      sample: {
        grpHdr: {
          msgId: "PAIN013-RTP-0001",
          creDtTm: "2026-06-24T10:00:00Z",
          nbOfTxs: "1",
          ctrlSum: "500.00",
          initgPty: {
            nm: "IremboGov",
            id: { orgId: { othr: { id: "IREMBO-001" } } }
          }
        },
        pmtInf: [{
          pmtInfId: "PMTINF-RTP-0001",
          pmtMtd: "TRF",
          reqdExctnDt: "2026-06-25",
          dbtr: { nm: "Alice Kamana" },
          dbtrAcct: { id: { othr: { id: "0788123456" } } },
          dbtrAgt: { finInstnId: { othr: { id: "078" } } },
          cdtTrfTx: [{
            pmtId: {
              endToEndId: "E2E-RTP-0001",
              instrId: "INSTR-RTP-0001"
            },
            amt: {
              instdAmt: { value: "500.00", ccy: "RWF" }
            },
            cdtrAgt: {
              finInstnId: { othr: { id: "040" } }
            },
            cdtr: {
              nm: "Rwanda Revenue Authority",
              id: { orgId: { othr: { id: "RRA-001" } } }
            },
            cdtrAcct: {
              id: { othr: { id: "0780000001" } },
              ccy: "RWF"
            },
            rmtInf: { ustrd: "Government tax payment — TIN 123456789" }
          }]
        }]
      },
      validationRules: {
        required: ['grpHdr', 'pmtInf'],
        grpHdr: ['msgId', 'creDtTm'],
        pmtInf: ['pmtInfId', 'pmtMtd', 'cdtr', 'cdtTrfTx']
      }
    },

    'pain.014': {
      name: 'Request to Pay Response (pain.014)',
      description: 'Creditor payment activation response — accepts or rejects a pain.013 RtP',
      sample: {
        grpHdr: {
          msgId: "PAIN014-RESP-0001",
          creDtTm: "2026-06-24T10:05:00Z",
          nbOfTxs: "1",
          initgPty: {
            nm: "Alice Kamana",
            id: { orgId: { othr: { id: "DBT-001" } } }
          }
        },
        orgnlGrpInfAndSts: {
          orgnlMsgId: "PAIN013-RTP-0001",
          orgnlMsgNmId: "pain.013.001.11",
          grpSts: "ACCP"
        },
        orgnlPmtInfAndSts: [{
          orgnlPmtInfId: "PMTINF-RTP-0001",
          pmtInfSts: "ACCP",
          txInfAndSts: [{
            orgnlEndToEndId: "E2E-RTP-0001",
            txSts: "ACCP"
          }]
        }]
      },
      validationRules: {
        required: ['grpHdr', 'orgnlGrpInfAndSts'],
        grpHdr: ['msgId', 'creDtTm'],
        orgnlGrpInfAndSts: ['orgnlMsgId', 'orgnlMsgNmId']
      }
    },

    'camt.056': {
      name: 'Payment Cancellation Request (camt.056)',
      description: 'Request to cancel a previously sent payment instruction (pacs.008)',
      sample: {
        assgnmt: {
          id: "ASSGN-CXL-0001",
          assgnr: { agt: { finInstnId: { othr: { id: "078" } } } },
          assgne: { agt: { finInstnId: { othr: { id: "040" } } } },
          creDtTm: "2026-06-24T10:00:00Z"
        },
        undrlyg: [{
          orgnlGrpInfAndCxl: {
            orgnlMsgId: "MSG-ORIG-001",
            orgnlMsgNmId: "pacs.008.001.11"
          },
          txInf: [{
            cxlId: "CXL-0001",
            orgnlInstrId: "INSTR-ORIG-001",
            orgnlEndToEndId: "E2E-ORIG-001",
            orgnlTxId: "TX-ORIG-001",
            cxlRsnInf: [{
              rsn: { cd: "DUPL" },
              addtlInf: "Duplicate payment detected"
            }]
          }]
        }]
      },
      validationRules: {
        required: ['assgnmt', 'undrlyg'],
        assgnmt: ['id', 'assgnr', 'assgne', 'creDtTm']
      }
    },

    'camt.029': {
      name: 'Resolution of Investigation (camt.029)',
      description: 'Response to a camt.056 Payment Cancellation Request — ACCP or RJCT',
      sample: {
        assgnmt: {
          id: "RSLN-0001",
          assgnr: { agt: { finInstnId: { othr: { id: "040" } } } },
          assgne: { agt: { finInstnId: { othr: { id: "078" } } } },
          creDtTm: "2026-06-24T10:30:00Z"
        },
        rslvdCase: {
          id: "ASSGN-CXL-0001",
          cretr: { agt: { finInstnId: { othr: { id: "040" } } } }
        },
        sts: {
          conf: "ACCP"
        },
        cxlDtls: [{
          orgnlGrpInfAndSts: {
            orgnlMsgId: "MSG-ORIG-001",
            orgnlMsgNmId: "pacs.008.001.11",
            grpCxlSts: "ACCR"
          },
          txInfAndSts: [{
            cxlStsId: "CXLSTS-0001",
            orgnlEndToEndId: "E2E-ORIG-001",
            orgnlTxId: "TX-ORIG-001",
            txCxlSts: "ACCR"
          }]
        }]
      },
      validationRules: {
        required: ['assgnmt', 'sts'],
        assgnmt: ['id', 'assgnr', 'assgne', 'creDtTm']
      }
    },

    'pacs.004': {
      name: 'Payment Return (pacs.004)',
      description: 'Returns funds for a previously settled payment — final step of a recall',
      sample: {
        grpHdr: {
          msgId: "PMTRTR-0001",
          creDtTm: "2026-06-24T11:00:00Z",
          nbOfTxs: "1",
          sttlmInf: { sttlmMtd: "CLRG" },
          instgAgt: { finInstnId: { othr: { id: "040" } } },
          instdAgt: { finInstnId: { othr: { id: "078" } } }
        },
        orgnlGrpInf: {
          orgnlMsgId: "MSG-ORIG-001",
          orgnlMsgNmId: "pacs.008.001.11"
        },
        txInf: [{
          rtrId: "RTR-0001",
          orgnlInstrId: "INSTR-ORIG-001",
          orgnlEndToEndId: "E2E-ORIG-001",
          orgnlTxId: "TX-ORIG-001",
          orgnlIntrBkSttlmAmt: { value: "1000.00", ccy: "RWF" },
          rtrdIntrBkSttlmAmt: { value: "1000.00", ccy: "RWF" },
          chrgBr: "SLEV",
          rtrRsnInf: [{
            rsn: { cd: "DUPL" },
            addtlInf: "Returning duplicate payment per accepted cancellation"
          }]
        }]
      },
      validationRules: {
        required: ['grpHdr', 'txInf'],
        grpHdr: ['msgId', 'creDtTm', 'nbOfTxs'],
        txInf: ['rtrdIntrBkSttlmAmt']
      }
    },

    'pacs.007': {
      name: 'Payment Reversal (pacs.007)',
      description: 'Reverses a previously settled payment, initiated by the instructing agent',
      sample: {
        grpHdr: {
          msgId: "PMTRVSL-0001",
          creDtTm: "2026-06-24T11:30:00Z",
          nbOfTxs: "1",
          sttlmInf: { sttlmMtd: "CLRG" },
          instgAgt: { finInstnId: { othr: { id: "078" } } },
          instdAgt: { finInstnId: { othr: { id: "040" } } }
        },
        orgnlGrpInf: {
          orgnlMsgId: "MSG-ORIG-001",
          orgnlMsgNmId: "pacs.008.001.11"
        },
        txInf: [{
          rvslId: "RVSL-0001",
          orgnlInstrId: "INSTR-ORIG-001",
          orgnlEndToEndId: "E2E-ORIG-001",
          orgnlTxId: "TX-ORIG-001",
          orgnlIntrBkSttlmAmt: { value: "1000.00", ccy: "RWF" },
          rvsdIntrBkSttlmAmt: { value: "1000.00", ccy: "RWF" },
          chrgBr: "SLEV",
          rvslRsnInf: [{
            rsn: { cd: "DUPL" },
            addtlInf: "Reversing duplicate payment instruction"
          }]
        }]
      },
      validationRules: {
        required: ['grpHdr', 'txInf'],
        grpHdr: ['msgId', 'creDtTm', 'nbOfTxs'],
        txInf: ['rvsdIntrBkSttlmAmt']
      }
    },

    'acmt.010': {
      name: 'Account Request Acknowledgement (acmt.010)',
      description: 'Approval response to an acmt.007 account registration request',
      sample: {
        msgId: "ACMT010-ACK-0001",
        orgId: "yourfsp",
        acctSvcr: "switch"
      },
      validationRules: {
        required: []
      }
    },

    'acmt.011': {
      name: 'Account Request Rejection (acmt.011)',
      description: 'Rejection response to an acmt.007 account registration request',
      sample: {
        msgId: "ACMT011-RJCT-0001",
        orgId: "yourfsp",
        acctSvcr: "switch"
      },
      validationRules: {
        required: []
      }
    },

    'acmt.014': {
      name: 'Account Report (acmt.014)',
      description: 'Account report for a registered participant organisation',
      sample: {
        msgId: "ACMT014-RPT-0001",
        acctSvcr: "switch",
        fullLglNm: "Participant DFSP",
        orgId: "yourfsp"
      },
      validationRules: {
        required: []
      }
    },

    'acmt.024': {
      name: 'Identification Verification Report (acmt.024)',
      description: 'Response to an acmt.023 identification verification request',
      sample: {
        msgId: "ACMT024-VRF-0001",
        assgnr: "switch",
        assgne: "yourfsp",
        orgnlId: "ACMT023-REQ-0001",
        verified: true
      },
      validationRules: {
        required: []
      }
    },
    'acmt.006': {
      name: 'Account Management Status Report (acmt.006)',
      description: 'Status of a pending account management request (e.g. an acmt.003 modification)',
      sample: {
        msgId: { id: "ACMT006-STS-0001" },
        rltdRef: { ref: "ACMT003-MOD-0001" },
        stsRpt: { sts: { sts: "RECE" } }
      },
      validationRules: { required: [] }
    },

    'acmt.020': {
      name: 'Account Closing Amendment Request (acmt.020)',
      description: 'Amends a pending acmt.019 account closing request (e.g. corrected account details)',
      sample: {
        refs: {
          msgId: { id: "ACMT020-AMD-0001" },
          prcId: { id: "PROC-CLO-0001" }
        },
        acctId: {
          id: { iban: "RW42040000000788123456" },
          ccy: "RWF"
        },
        acctSvcrId: {
          finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } }
        },
        orgId: { othr: { id: "ORG-JANE-DOE-001" } }
      },
      validationRules: { required: [] }
    },

    'acmt.023': {
      name: 'Identification Verification Request (acmt.023)',
      description: 'Asks the counterparty to confirm a party/account identification before payment (confirmation of payee)',
      sample: {
        assgnmt: {
          msgId: "ACMT023-VRF-0001",
          assgnr: { pty: { nm: "Equity Bank Rwanda" } },
          assgne: { pty: { nm: "Bank of Kigali" } }
        },
        vrfctn: { id: "VRF-0788123456" }
      },
      validationRules: { required: [] }
    },

    'admi.002': {
      name: 'Message Reject (admi.002)',
      description: 'Administrative rejection of a received message, with the reference of the offending message',
      sample: {
        rltdRef: { ref: "MSG-ORIG-001" },
        rsn: { rjctgPtyRsn: "Schema validation failed" }
      },
      validationRules: { required: [] }
    },

    'admi.005': {
      name: 'Report Query Request (admi.005)',
      description: 'Queries the switch for a system report',
      sample: {
        msgHdr: { msgId: "ADMI005-QRY-0001" }
      },
      validationRules: { required: [] }
    },

    'admi.006': {
      name: 'Resend Request (admi.006)',
      description: 'Asks a counterparty to resend a previously transmitted message',
      sample: {
        msgHdr: { msgId: "ADMI006-RSND-0001" },
        rsndSchCrit: { rcpt: { id: { anyBIC: "BKIGRWRW" } } }
      },
      validationRules: { required: [] }
    },

    'camt.004': {
      name: 'Return Account (camt.004)',
      description: 'Response to a camt.003 account query — this minimal form carries the error branch (account not found)',
      sample: {
        msgHdr: { msgId: "CAMT004-RSP-0001" },
        rptOrErr: { oprlErr: { err: { cd: "X050" } } }
      },
      validationRules: { required: [] }
    },

    'camt.053': {
      name: 'Bank To Customer Statement (camt.053)',
      description: 'End-of-day account statement with the closing booked balance',
      sample: {
        grpHdr: { msgId: "CAMT053-STMT-0001" },
        stmt: {
          id: "STMT-2026-07-22-0788123456",
          acct: { nm: "Alice Kamana — Savings" },
          bal: {
            tp: { cdOrPrtry: { cd: "CLBD" } },
            amt: "1250000.00",
            cdtDbtInd: "CRDT"
          }
        }
      },
      validationRules: { required: [] }
    },

    'camt.060': {
      name: 'Account Reporting Request (camt.060)',
      description: 'Requests an account report or statement (here: a camt.053 statement) from the account servicer',
      sample: {
        grpHdr: { msgId: "CAMT060-REQ-0001" },
        rptgReq: {
          reqdMsgNmId: "camt.053.001.12",
          acctOwnr: { pty: { nm: "Alice Kamana" } }
        }
      },
      validationRules: { required: [] }
    },

    'camt.110': {
      name: 'Investigation Request (camt.110)',
      description: 'Opens an investigation (request for information) on a payment with the counterparty',
      sample: {
        invstgtnReq: {
          msgId: "CAMT110-INV-0001",
          invstgtnTp: { cd: "RQFI" },
          rqstr: { pty: { nm: "Equity Bank Rwanda" } },
          rspndr: { pty: { nm: "Bank of Kigali" } }
        },
        invstgtnData: { rsn: { cd: "MS23" } }
      },
      validationRules: { required: [] }
    },

    'camt.111': {
      name: 'Investigation Response (camt.111)',
      description: 'Closes a camt.110 investigation with its outcome status',
      sample: {
        invstgtnRspn: {
          msgId: "CAMT111-RSP-0001",
          invstgtnSts: { sts: "CLSD" }
        },
        orgnlInvstgtnReq: {
          msgId: "CAMT110-INV-0001",
          invstgtnTp: { cd: "RQFI" },
          rqstr: { pty: { nm: "Equity Bank Rwanda" } },
          rspndr: { pty: { nm: "Bank of Kigali" } }
        }
      },
      validationRules: { required: [] }
    },

    'pacs.009': {
      name: 'Financial Institution Credit Transfer (pacs.009)',
      description: 'Bank-to-bank credit transfer (e.g. interbank liquidity/settlement movement)',
      sample: {
        grpHdr: {
          msgId: "PACS009-FICT-0001",
          sttlmInf: { sttlmMtd: "CLRG" },
          instgAgt: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } },
          instdAgt: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } }
        },
        cdtTrfTxInf: {
          pmtId: { instrId: "INSTR-FICT-0001", endToEndId: "E2E-FICT-0001" },
          intrBkSttlmAmt: "5000000.00",
          dbtr: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } },
          cdtr: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } },
          cdtrAcct: { nm: "EQ Settlement Account" },
          rmtInf: { ustrd: "Interbank settlement — clearing cycle 2026-07-22" }
        }
      },
      validationRules: { required: [] }
    },

    'pain.001': {
      name: 'Customer Credit Transfer Initiation (pain.001)',
      description: 'Customer instructs their bank to pay: Alice pays her RRA tax bill via IremboGov',
      sample: {
        grpHdr: {
          msgId: "PAIN001-CCT-0001",
          initgPty: { nm: "IremboGov" }
        },
        pmtInf: {
          pmtInfId: "PMTINF-CCT-0001",
          pmtMtd: "TRF",
          dbtr: { nm: "Alice Kamana" },
          dbtrAcct: { nm: "Alice Kamana — Current" },
          dbtrAgt: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } },
          cdtTrfTxInf: {
            pmtId: { instrId: "INSTR-CCT-0001", endToEndId: "E2E-CCT-0001" },
            amt: { instdAmt: "500.00" },
            cdtrAgt: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } },
            cdtr: { nm: "Rwanda Revenue Authority" },
            cdtrAcct: { nm: "RRA Collections" },
            rmtInf: { ustrd: "Tax payment — TIN 123456789" }
          }
        }
      },
      validationRules: { required: [] }
    },

    'pain.002': {
      name: 'Customer Payment Status Report (pain.002)',
      description: 'Bank reports the status of a customer pain.001 initiation back to the initiating party',
      sample: {
        grpHdr: {
          msgId: "PAIN002-STS-0001",
          dbtrAgt: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } },
          cdtrAgt: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } }
        },
        orgnlGrpInfAndSts: {
          orgnlMsgId: "PAIN001-CCT-0001",
          orgnlMsgNmId: "pain.001.001.11"
        }
      },
      validationRules: { required: [] }
    },

    'pain.007': {
      name: 'Customer Payment Reversal (pain.007)',
      description: 'Customer-side reversal of a previously initiated payment (e.g. duplicate submission)',
      sample: {
        grpHdr: {
          msgId: "PAIN007-RVSL-0001",
          dbtrAgt: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } },
          cdtrAgt: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } }
        },
        orgnlGrpInf: {
          orgnlMsgId: "PAIN001-CCT-0001",
          orgnlMsgNmId: "pain.001.001.11"
        }
      },
      validationRules: { required: [] }
    },

    'pain.008': {
      name: 'Customer Direct Debit Initiation (pain.008)',
      description: 'Creditor collects from a debtor by direct debit: utility company collects a monthly bill',
      sample: {
        grpHdr: {
          msgId: "PAIN008-DD-0001",
          initgPty: { nm: "Kigali Water & Power" }
        },
        pmtInf: {
          pmtInfId: "PMTINF-DD-0001",
          cdtr: { nm: "Kigali Water & Power" },
          cdtrAcct: { nm: "KWP Collections" },
          cdtrAgt: { finInstnId: { nm: "Bank of Kigali", othr: { id: "040" } } },
          drctDbtTxInf: {
            pmtId: { instrId: "INSTR-DD-0001", endToEndId: "E2E-DD-0001" },
            instdAmt: "12500.00",
            dbtrAgt: { finInstnId: { nm: "Equity Bank Rwanda", othr: { id: "078" } } },
            dbtr: { nm: "Mike Tango" },
            dbtrAcct: { nm: "Mike Tango — Current" },
            rmtInf: { ustrd: "Utility bill July 2026 — meter 40412345" }
          }
        }
      },
      validationRules: { required: [] }
    }
  };
  
  /**
   * Validation field mappings - common date/time fields across all message types
   * 
   * These fields are validated for ISO 8601 format:
   * - Date only: YYYY-MM-DD (e.g., "2000-01-01", "1970-01-01")
   * - DateTime with timezone: YYYY-MM-DDTHH:MM:SSZ (e.g., "2025-12-02T11:54:00Z")
   * - DateTime with offset: YYYY-MM-DDTHH:MM:SS+HH:MM (e.g., "2025-12-02T11:54:00+03:00")
   * 
   * Note: The frontend validation accepts both formats. Backend validation may have
   * additional requirements based on the specific message type and field semantics.
   */
  export const DATE_TIME_FIELDS = [
    'creDtTm',           // Creation Date Time - requires time component
    'creationDateTime',  // Creation Date Time - requires time component
    'acceptanceDateTime' // Acceptance Date Time - requires time component
  ];

  /**
   * Fields typed as xs:date in the ISO 20022 schemas — they must stay
   * YYYY-MM-DD. Stamping a full dateTime into these produces XSD violations
   * ("... is not a valid value for 'date'").
   */
  export const DATE_ONLY_FIELDS = [
    'birthDt',           // Birth Date
    'registrationDate',  // Registration Date (RegnDt)
    'regnDt',            // Registration Date (short key)
    'sttlmDt',           // Settlement Date
    'reqdExctnDt'        // Requested Execution Date
  ];
  
  /**
   * Status codes by message type
   * Useful for validating status fields
   */
  export const STATUS_CODES = {
    'pacs.002': {
      groupStatus: ['ACCP', 'RJCT', 'PDNG', 'PART'],
      transactionStatus: ['ACCP', 'ACSC', 'ACSP', 'ACWC', 'RJCT', 'PDNG']
    },
    'acmt.007': {
      accountStatus: ['ENAB', 'DISB', 'PNDG', 'CLSD']
    }
  };
  
  /**
   * Common ISO codes
   */
  export const ISO_CODES = {
    // ISO 4217 Currency Codes (Rwanda-relevant subset)
    currencies: ['RWF', 'USD', 'EUR', 'GBP', 'KES', 'UGX', 'TZS'],
    
    // ISO 3166 Country Codes (Rwanda and neighbors)
    countries: ['RW', 'KE', 'UG', 'TZ', 'BI', 'CD'],
    
    // Common settlement methods
    settlementMethods: ['CLRG', 'INDA', 'INGA', 'COVE']
  };
  
  /**
   * Helper function to get message sample
   * @param {string} messageType - The message type (e.g., 'pacs.008')
   * @returns {Object|null} The message sample or null if not found
   */
  export const getMessageSample = (messageType) => {
    return MESSAGE_SAMPLES[messageType] || null;
  };
  
  /**
   * Helper function to get validation rules
   * @param {string} messageType - The message type
   * @returns {Object|null} The validation rules or null if not found
   */
  export const getValidationRules = (messageType) => {
    const sample = MESSAGE_SAMPLES[messageType];
    return sample ? sample.validationRules : null;
  };
  
  /**
   * Helper function to generate dynamic IDs with timestamp
   * @param {string} prefix - ID prefix (e.g., 'MSG', 'TX', 'E2E')
   * @returns {string} Generated ID
   */
  export const generateMessageId = (prefix = 'MSG') => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000).toString().padStart(4, '0');
    return `${prefix}-${timestamp}-${random}`;
  };
  
  /**
   * Helper function to get current ISO 8601 timestamp
   * @returns {string} Current timestamp in ISO 8601 format
   */
  export const getCurrentTimestamp = () => {
    return new Date().toISOString();
  };
  
  /**
   * Returns a copy of a message body with new message, transaction, end-to-end and
   * instruction ids and a current creation time. References to an earlier message
   * (orgnlMsgId, orgnlEndToEndId, …) are different keys and are left alone.
   *
   * The generator runs this before every Generate: reusing the ids of a message
   * already signed makes the switch treat the second one as a replay, and the
   * engine refuses to store it twice.
   * @param {Object} body - Message body as JSON
   * @returns {Object} Copy with fresh ids and timestamps
   */
  export const withFreshIds = (body) => {
    const freshSample = JSON.parse(JSON.stringify(body));

    // Update common fields with fresh values
    const updateTimestamps = (obj) => {
      Object.keys(obj).forEach(key => {
        if (DATE_TIME_FIELDS.includes(key) && typeof obj[key] === 'string') {
          obj[key] = getCurrentTimestamp();
        } else if (DATE_ONLY_FIELDS.includes(key) && typeof obj[key] === 'string') {
          // xs:date fields keep their sample value — historical dates (birth,
          // registration) must not be replaced with "today", and the format
          // must stay YYYY-MM-DD.
        } else if (key === 'msgId' && typeof obj[key] === 'string') {
          obj[key] = generateMessageId('MSG');
        } else if (key === 'txId' && typeof obj[key] === 'string') {
          obj[key] = generateMessageId('TX');
        } else if (key === 'endToEndId' && typeof obj[key] === 'string') {
          obj[key] = generateMessageId('E2E');
        } else if (key === 'instrId' && typeof obj[key] === 'string') {
          obj[key] = generateMessageId('INSTR');
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            obj[key].forEach(item => {
              if (typeof item === 'object' && item !== null) {
                updateTimestamps(item);
              }
            });
          } else {
            updateTimestamps(obj[key]);
          }
        }
      });
    };
  
    updateTimestamps(freshSample);
    return freshSample;
  };

  /**
   * Helper function to create a fresh sample with updated timestamps and IDs
   * @param {string} messageType - The message type
   * @returns {Object} Fresh sample with updated values
   */
  export const createFreshSample = (messageType) => {
    const sample = MESSAGE_SAMPLES[messageType];
    if (!sample) return null;
    return withFreshIds(sample.sample);
  };
  
  /**
   * List all available message types
   * @returns {Array<Object>} Array of message type info
   */
export const listMessageTypes = () => {
    return Object.keys(MESSAGE_SAMPLES).map(key => ({
      type: key,
      name: MESSAGE_SAMPLES[key].name,
      description: MESSAGE_SAMPLES[key].description
    }));
};
